import {
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { TableTooltipComponent } from './table-tooltip.component';
import { TariffDetails } from '../../table/main-table/column-content/column-content.container';
import { TableTooltipBasisComponent } from './table-tooltip-basis.component';
import { MainTableColumnId } from '@prlw/core/monitor/types/main-table';
import { BasisDetailsInterface } from '../../basis-details.interface';
import { ModifierType } from '@prlw/core/monitor/types/common';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';

const OFFSET_X_FIRST = -25;
const OFFSET_X_SECOND = -33;
const OFFSET_Y_BOTTOM = 14;
const OFFSET_Y_TOP = -13;

@Directive({
  selector: '[prlwTableTooltip]',
})
export class TableTooltipDirective implements OnDestroy {
  @Input('prlwTableTooltip')
  public columnId: MainTableColumnId | null = null;
  @Input()
  public isSecond: boolean = false;
  @Input()
  public isDealTotal: boolean = false;
  @Input()
  public dealTime: string | null = null;
  @Input()
  public prevDealDate: string | null = null;
  @Input()
  public lastQuoteTime: string | null = null;
  @Input()
  public basisDetails: BasisDetailsInterface | null = null;
  @Input()
  public tariffDetails: TariffDetails | null = null;
  @Input()
  public modifierType!: ModifierType | null;
  @Input()
  public mode: string | null = null;

  private _overlayRef?: OverlayRef;
  private _tooltipRef!:
    | EmbeddedViewRef<TableTooltipComponent>
    | ComponentRef<TableTooltipComponent>
    | EmbeddedViewRef<TableTooltipBasisComponent>
    | ComponentRef<TableTooltipBasisComponent>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly overlay: Overlay,
    private readonly responsiveService: ResponsiveService,
  ) {}

  @HostListener('mouseenter')
  public open(): void {
    if (this.responsiveService.xsmall$.getValue()) {
      return;
    }
    if (this.columnId === null) {
      return;
    }
    const offsetX = this.isSecond ? OFFSET_X_SECOND : OFFSET_X_FIRST;
    const originX = this.isSecond ? 'end' : 'start';
    const overlayConfig = new OverlayConfig({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this._elementRef.nativeElement)
        .withPositions([
          {
            originX,
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: OFFSET_Y_BOTTOM,
            offsetX,
            panelClass: 'bottom',
          },
          {
            originX,
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: OFFSET_Y_TOP,
            offsetX,
            panelClass: 'top',
          },
        ])
        .withPush(false),
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });
    this._overlayRef = this.overlay.create(overlayConfig);

    if (this.basisDetails) {
      const modalPortal = new ComponentPortal(TableTooltipBasisComponent);
      this._tooltipRef = this._overlayRef.attach(modalPortal);
      this._tooltipRef.instance.basisDetails = this.basisDetails;
    } else {
      const modalPortal = new ComponentPortal(TableTooltipComponent);
      this._tooltipRef = this._overlayRef.attach(modalPortal);
      if (!this.tariffDetails) {
        this._tooltipRef.instance.text = this._getTooltipText(
          this.columnId,
          this.isSecond,
          this.isDealTotal,
          this.dealTime,
          this.prevDealDate,
          this.lastQuoteTime,
          this.mode,
        );
      } else if (
        this.mode === 'light' &&
        this.columnId === MainTableColumnId.TARIFF
      ) {
        this._tooltipRef.instance.text = 'Доступно в PRO';
      } else {
        this._tooltipRef.instance.tariffDetails = this.tariffDetails;
      }
    }

    this._overlayRef
      .keydownEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.close();
        }
      });
  }

  @HostListener('mouseleave')
  public close(): void {
    this._overlayRef?.detach();
    this.destroy$.next();
  }

  private _getTooltipText(
    columnId: MainTableColumnId,
    isSecond: boolean,
    isDealTotal: boolean,
    dealTime: string | null,
    prevDealDate: string | null,
    lastQuoteTime: string | null,
    mode: string | null,
  ): string {
    const tooltipText: { [key: number]: string } = {
      [MainTableColumnId.OFFER]: 'Минимальная цена предложений по базису',
      [MainTableColumnId.DEAL]: 'Цена последней сделки по базису',
      [MainTableColumnId.DEMAND]: 'Максимальная цена спросов по базису',
      [MainTableColumnId.CHANGES]:
        'Изменение цены относительно цены закрытия прошлой сессии',
      [MainTableColumnId.VOLUME]: 'Реализованный объем на текущий момент',
      [MainTableColumnId.PREV_SESSION_VOLUME]:
        'Реализованный объем торговой сессии',
      [MainTableColumnId.PREV_DEALS_AMOUNT_DIFF]:
        'Разница в объемах по отношению к предыдущему торговому дню',
      [MainTableColumnId.TARIFF]: '',
      [MainTableColumnId.SHIPMENT]: 'Ожидаемый срок отгрузки в днях',
      [MainTableColumnId.ON_THE_WAY]:
        'Ожидаемый срок доставки до станции назначения в днях',
      [MainTableColumnId.TRADE_START]: 'Ожидаемое время начала продаж',
      [MainTableColumnId.BRIEF_DESCRIPTION]: 'Название базиса',
    };

    const tooltipTextSecond: { [key: number]: string } = {
      [MainTableColumnId.OFFER]:
        'Количество тонн, выставленных на продажу на данный момент',
      [MainTableColumnId.DEMAND]:
        'Количество тонн, выставленных на покупку на данный момент',
      [MainTableColumnId.VOLUME]: 'Ожидаемый прогноз реализации',
    };

    const tooltipTextForLocalPrice: { [key: string]: string } = {
      [ModifierType.BASIC]: 'Сумма последней сделки и тарифа',
      [ModifierType.PREPAID]: 'Сумма последней сделки, тарифа и маржи',
      [ModifierType.DEFER]:
        'Сумма последней сделки и тарифа, с учетом отсрочки платежа',
    };

    if (
      mode === 'light' &&
      [
        MainTableColumnId.LOCAL_PRICE,
        MainTableColumnId.SHIPMENT,
        MainTableColumnId.ON_THE_WAY,
      ].includes(columnId)
    ) {
      return 'Доступно в PRO';
    }
    if (columnId === MainTableColumnId.LOCAL_PRICE) {
      return tooltipTextForLocalPrice[this.modifierType ?? ModifierType.BASIC];
    }
    if (isDealTotal) {
      return 'Сумма сделки и тарифа';
    }
    if (dealTime) {
      return `${tooltipText[columnId]}: ${dealTime}`;
    }
    if (prevDealDate) {
      return `${tooltipText[columnId]}: ${prevDealDate}`;
    }
    if (lastQuoteTime) {
      return `${tooltipText[columnId]}: ${lastQuoteTime}`;
    }
    return isSecond ? tooltipTextSecond[columnId] : tooltipText[columnId];
  }

  public ngOnDestroy(): void {
    this.close();
    this.destroy$.complete();
  }
}
